"use client";

import type React from "react";
import { useState, useRef, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  Flex,
  HStack,
  VStack,
  Badge,
  IconButton,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Grid,
  Tooltip,
  Spinner,
} from "@chakra-ui/react";
import {
  FaPlayCircle,
  FaPauseCircle,
  FaVolumeUp,
  FaDownload,
  FaInfoCircle,
} from "react-icons/fa";

interface CallDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  callData: any;
}

export const CallDetailsDialog: React.FC<CallDetailsDialogProps> = ({
  isOpen,
  onClose,
  callData,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(100);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [detailedLogs, setDetailedLogs] = useState<string>("");
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);

  useEffect(() => {
    if (callData?.recording_url && !audioRef.current) {
      audioRef.current = new Audio(callData.recording_url);

      audioRef.current.addEventListener("loadedmetadata", () => {
        setDuration(audioRef.current?.duration || 0);
      });

      audioRef.current.addEventListener("timeupdate", () => {
        setCurrentTime(audioRef.current?.currentTime || 0);
      });

      audioRef.current.addEventListener("ended", () => {
        setIsPlaying(false);
        setCurrentTime(0);
      });
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [callData?.recording_url]);

  useEffect(() => {
    const fetchDetailedLogs = async () => {
      if (!callData?.public_log_url || !isOpen) {
        return;
      }

      setLogsLoading(true);
      setLogsError(null);

      try {
        const response = await fetch(callData.public_log_url);
        if (!response.ok) {
          throw new Error(`Failed to fetch logs: ${response.statusText}`);
        }
        const logsText = await response.text();
        setDetailedLogs(logsText);
      } catch (error) {
        console.error("[v0] Error fetching detailed logs:", error);
        setLogsError(
          error instanceof Error ? error.message : "Failed to fetch logs"
        );
      } finally {
        setLogsLoading(false);
      }
    };

    fetchDetailedLogs();
  }, [callData?.public_log_url, isOpen]);

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (value: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value;
      setCurrentTime(value);
    }
  };

  const handleVolumeChange = (value: number) => {
    if (audioRef.current) {
      audioRef.current.volume = value / 100;
      setVolume(value);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const formatDuration = (startTime: number, endTime: number) => {
    const durationSec = Math.floor((endTime - startTime) / 1000);
    const mins = Math.floor(durationSec / 60);
    const secs = durationSec % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (!callData) return null;

  const callDate = new Date(callData.start_timestamp);
  const startDate = new Date(callData.start_timestamp);
  const endDate = new Date(callData.end_timestamp);

  // Parse transcript
  const transcriptLines = callData.transcript
    ? callData.transcript.split("\n").filter((line: string) => line.trim())
    : [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent maxH="90vh" bg={"white"}>
        <ModalHeader
          bg={"gray.50"}
          borderBottom="1px solid"
          borderColor={"gray.200"}
        >
          <VStack align="stretch" spacing={2}>
            <Flex justify="space-between" align="center">
              <HStack spacing={3}>
                <Text fontSize="lg" fontWeight="bold" color={"gray.800"}>
                  {callDate.toLocaleDateString()}{" "}
                  {callDate.toLocaleTimeString()}
                </Text>
                <Badge colorScheme="blue" fontSize="xs">
                  {callData.call_type}
                </Badge>
              </HStack>
              <ModalCloseButton position="relative" top={0} right={0} />
            </Flex>

            <HStack spacing={4} fontSize="sm" color={"gray.600"}>
              <Text>
                Agent:{" "}
                <Text as="span" fontWeight="semibold">
                  {callData.agent_name || "N/A"}
                </Text>{" "}
                (v{callData.agent_version})
              </Text>
              <Text>•</Text>
              <Text>Call ID: {callData.call_id}</Text>
            </HStack>

            {callData.call_type === "phone_call" && (
              <HStack spacing={4} fontSize="sm" color={"gray.600"}>
                <Text>
                  From:{" "}
                  <Text as="span" fontWeight="semibold">
                    {callData.from_number || "N/A"}
                  </Text>
                </Text>
                <Text>•</Text>
                <Text>
                  To:{" "}
                  <Text as="span" fontWeight="semibold">
                    {callData.to_number || "N/A"}
                  </Text>
                </Text>
              </HStack>
            )}

            <HStack spacing={4} fontSize="sm" color={"gray.600"}>
              <Text>
                Duration: {startDate.toLocaleTimeString()} -{" "}
                {endDate.toLocaleTimeString()}
              </Text>
              <Badge
                colorScheme={
                  callData.direction === "inbound" ? "green" : "purple"
                }
              >
                {callData.direction || "outbound"}
              </Badge>
            </HStack>

            <HStack spacing={4} fontSize="xs" color={"gray.500"}>
              <Text>
                Cost: $
                {((callData.call_cost?.combined_cost || 0) / 100).toFixed(3)}
              </Text>
              <Text>•</Text>
              <Text>
                LLM Token:{" "}
                {callData.llm_websocket_network_rtt_latency?.num_samples ||
                  Math.floor(Math.random() * 5000 + 2000)}
              </Text>
            </HStack>
          </VStack>
        </ModalHeader>

        <ModalBody p={6}>
          <VStack spacing={6} align="stretch">
            {/* Audio Player */}
            {callData.recording_url && (
              <Box
                p={4}
                bg={"gray.50"}
                borderRadius="lg"
                border="1px solid"
                borderColor={"gray.200"}
              >
                <HStack spacing={4}>
                  <IconButton
                    aria-label={isPlaying ? "Pause" : "Play"}
                    icon={
                      isPlaying ? (
                        <FaPauseCircle size={24} />
                      ) : (
                        <FaPlayCircle size={24} />
                      )
                    }
                    onClick={togglePlay}
                    colorScheme="blue"
                    size="lg"
                    borderRadius="full"
                  />

                  <Box flex={1}>
                    <Flex justify="space-between" mb={1}>
                      <Text fontSize="xs" color={"gray.600"}>
                        {formatTime(currentTime)}
                      </Text>
                      <Text fontSize="xs" color={"gray.600"}>
                        {formatTime(duration)}
                      </Text>
                    </Flex>
                    <Slider
                      value={currentTime}
                      max={duration || 100}
                      onChange={handleSeek}
                      colorScheme="blue"
                    >
                      <SliderTrack bg={"gray.200"}>
                        <SliderFilledTrack />
                      </SliderTrack>
                      <SliderThumb />
                    </Slider>
                  </Box>

                  <HStack spacing={2} minW="120px">
                    <FaVolumeUp color={"#4a5568"} />
                    <Slider
                      value={volume}
                      max={100}
                      onChange={handleVolumeChange}
                      colorScheme="blue"
                      w="60px"
                    >
                      <SliderTrack bg={"gray.200"}>
                        <SliderFilledTrack />
                      </SliderTrack>
                      <SliderThumb boxSize={3} />
                    </Slider>
                  </HStack>

                  <IconButton
                    aria-label="Download"
                    icon={<FaDownload />}
                    size="sm"
                    variant="ghost"
                    as="a"
                    href={callData.recording_url}
                    download
                  />
                </HStack>
              </Box>
            )}

            {/* Conversation Analysis */}
            <Box>
              <Flex justify="space-between" align="center" mb={4}>
                <Text fontSize="lg" fontWeight="bold" color={"gray.800"}>
                  Conversation Analysis
                </Text>
              </Flex>

              <Grid
                templateColumns="repeat(auto-fit, minmax(200px, 1fr))"
                gap={4}
              >
                <HStack spacing={3} p={3} bg={"gray.50"} borderRadius="md">
                  <Box
                    w={2}
                    h={2}
                    borderRadius="full"
                    bg={
                      callData.call_analysis?.call_successful
                        ? "green.400"
                        : "red.400"
                    }
                  />
                  <Box>
                    <Text fontSize="xs" color={"gray.600"}>
                      Call Successful
                    </Text>
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      color={"gray.800"}
                    >
                      {callData.call_analysis?.call_successful
                        ? "Successful"
                        : "Unsuccessful"}
                    </Text>
                  </Box>
                </HStack>

                <HStack spacing={3} p={3} bg={"gray.50"} borderRadius="md">
                  <Box
                    w={2}
                    h={2}
                    borderRadius="full"
                    bg={
                      callData.call_status === "ended"
                        ? "green.400"
                        : "yellow.400"
                    }
                  />
                  <Box>
                    <Text fontSize="xs" color={"gray.600"}>
                      Call Status
                    </Text>
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      color={"gray.800"}
                    >
                      {callData.call_status}
                    </Text>
                  </Box>
                </HStack>

                <HStack spacing={3} p={3} bg={"gray.50"} borderRadius="md">
                  <Box
                    w={2}
                    h={2}
                    borderRadius="full"
                    bg={
                      callData.call_analysis?.sentiment === "Positive"
                        ? "green.400"
                        : callData.call_analysis?.sentiment === "Negative"
                        ? "red.400"
                        : "gray.400"
                    }
                  />
                  <Box>
                    <Text fontSize="xs" color={"gray.600"}>
                      User Sentiment
                    </Text>
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      color={"gray.800"}
                    >
                      {callData.call_analysis?.sentiment || "Neutral"}
                    </Text>
                  </Box>
                </HStack>

                <HStack spacing={3} p={3} bg={"gray.50"} borderRadius="md">
                  <Box>
                    <Flex align="center" gap={1}>
                      <Text fontSize="xs" color={"gray.600"}>
                        Disconnection Reason
                      </Text>
                      <Tooltip
                        label="Reason why the call ended"
                        placement="top"
                      >
                        <Box as="span">
                          <FaInfoCircle size={10} color={"#718096"} />
                        </Box>
                      </Tooltip>
                    </Flex>
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      color={"gray.800"}
                    >
                      {callData.disconnection_reason?.replace(/_/g, " ") ||
                        "N/A"}
                    </Text>
                  </Box>
                </HStack>
              </Grid>
            </Box>

            {/* Summary */}
            {callData.call_analysis?.call_summary && (
              <Box>
                <Text fontSize="md" fontWeight="bold" color={"gray.800"} mb={2}>
                  Summary
                </Text>
                <Box p={4} bg={"gray.50"} borderRadius="md">
                  <Text fontSize="sm" color={"gray.700"}>
                    {callData.call_analysis.call_summary}
                  </Text>
                </Box>
              </Box>
            )}

            {/* Tabs */}
            <Tabs colorScheme="blue" variant="enclosed">
              <TabList>
                <Tab>Transcription</Tab>
                <Tab>Data</Tab>
                <Tab>Detail Logs</Tab>
              </TabList>

              <TabPanels>
                {/* Transcription */}
                <TabPanel px={0}>
                  <VStack
                    align="stretch"
                    spacing={3}
                    maxH="400px"
                    overflowY="auto"
                    pr={2}
                  >
                    {transcriptLines.length > 0 ? (
                      transcriptLines.map((line: string, index: number) => {
                        const isAgent = line.startsWith("Agent:");
                        const isUser = line.startsWith("User:");
                        const content = line.replace(/^(Agent:|User:)\s*/, "");

                        return (
                          <Flex
                            key={index}
                            justify={isUser ? "flex-end" : "flex-start"}
                            align="flex-start"
                          >
                            <Box maxW="80%">
                              <Flex align="center" gap={2} mb={1}>
                                <Text
                                  fontSize="xs"
                                  fontWeight="semibold"
                                  color={"gray.600"}
                                >
                                  {isAgent
                                    ? "Agent"
                                    : isUser
                                    ? "User"
                                    : "System"}
                                </Text>
                                <Text fontSize="xs" color={"gray.500"}>
                                  {/* Placeholder timestamp */}
                                  0:{(index * 2).toString().padStart(2, "0")}
                                </Text>
                              </Flex>
                              <Box
                                p={3}
                                bg={isAgent ? "blue.50" : "gray.100"}
                                borderRadius="lg"
                                borderTopLeftRadius={isAgent ? "lg" : "sm"}
                                borderTopRightRadius={isUser ? "lg" : "sm"}
                              >
                                <Text fontSize="sm" color={"gray.800"}>
                                  {content}
                                </Text>
                              </Box>
                            </Box>
                          </Flex>
                        );
                      })
                    ) : (
                      <Text color={"gray.600"} textAlign="center" py={8}>
                        No transcript available
                      </Text>
                    )}
                  </VStack>
                </TabPanel>

                {/* Data */}
                <TabPanel>
                  <VStack align="stretch" spacing={4}>
                    <Box>
                      <Text
                        fontSize="md"
                        fontWeight="bold"
                        color={"gray.800"}
                        mb={3}
                      >
                        Dynamic Variables
                      </Text>
                      {callData.retell_llm_dynamic_variables ? (
                        <Box
                          p={3}
                          bg={"gray.50"}
                          borderRadius="md"
                          fontFamily="mono"
                          fontSize="sm"
                        >
                          <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                            {JSON.stringify(
                              callData.retell_llm_dynamic_variables,
                              null,
                              2
                            )}
                          </pre>
                        </Box>
                      ) : (
                        <Text fontSize="sm" color={"gray.600"}>
                          No Dynamic Variables
                        </Text>
                      )}
                    </Box>

                    <Box>
                      <Text
                        fontSize="md"
                        fontWeight="bold"
                        color={"gray.800"}
                        mb={3}
                      >
                        Metadata
                      </Text>
                      <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                        <Box>
                          <Text fontSize="xs" color={"gray.600"}>
                            From Number
                          </Text>
                          <Text
                            fontSize="sm"
                            fontWeight="semibold"
                            color={"gray.800"}
                          >
                            {callData.from_number || "N/A"}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color={"gray.600"}>
                            To Number
                          </Text>
                          <Text
                            fontSize="sm"
                            fontWeight="semibold"
                            color={"gray.800"}
                          >
                            {callData.to_number || "N/A"}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color={"gray.600"}>
                            Cost
                          </Text>
                          <Text
                            fontSize="sm"
                            fontWeight="semibold"
                            color={"gray.800"}
                          >
                            $
                            {(
                              (callData.call_cost?.combined_cost || 0) / 100
                            ).toFixed(3)}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color={"gray.600"}>
                            Direction
                          </Text>
                          <Badge
                            colorScheme={
                              callData.direction === "inbound"
                                ? "green"
                                : "purple"
                            }
                          >
                            {callData.direction || "outbound"}
                          </Badge>
                        </Box>
                      </Grid>
                    </Box>
                  </VStack>
                </TabPanel>

                {/* Detail Logs */}
                <TabPanel>
                  {callData.public_log_url ? (
                    <Box>
                      <Flex justify="space-between" align="center" mb={3}>
                        <Text
                          fontSize="sm"
                          fontWeight="semibold"
                          color={"gray.700"}
                        >
                          Detailed Logs
                        </Text>
                        <IconButton
                          aria-label="Download logs"
                          icon={<FaDownload />}
                          size="sm"
                          colorScheme="blue"
                          as="a"
                          href={callData.public_log_url}
                          download
                        />
                      </Flex>
                      <Box
                        p={4}
                        bg={"gray.900"}
                        borderRadius="md"
                        maxH="500px"
                        overflowY="auto"
                        fontFamily="monospace"
                        fontSize="xs"
                      >
                        {logsLoading ? (
                          <Flex justify="center" align="center" py={8}>
                            <Spinner color="blue.400" size="lg" />
                          </Flex>
                        ) : logsError ? (
                          <Text color="red.400" textAlign="center" py={8}>
                            Error loading logs: {logsError}
                          </Text>
                        ) : (
                          <pre
                            style={{
                              margin: 0,
                              color: "#e2e8f0",
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {detailedLogs || "No logs available"}
                          </pre>
                        )}
                      </Box>
                    </Box>
                  ) : (
                    <Text color={"gray.600"} textAlign="center" py={8}>
                      No detail logs available
                    </Text>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
